import {
  Box,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { useMemo, type FC, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterTag } from '$/components/core/Collection/MaterialPresentation/DataSortingRow/components/FilterTag';
import { GenericTagContent } from '$/components/core/Collection/MaterialPresentation/DataSortingRow/components/GenericTagContent';
import { ShowAdditionalFilterTagsButton } from '$/components/core/Collection/MaterialPresentation/DataSortingRow/components/ShowAdditionalFilterTagsButton';
import { useCollapsibleList } from '$/hooks/useCollapsibleList';
import {
  useDesignGuideStore,
  type HarmonyFacet,
} from '$/pages/DashboardPages/pages/DesignGuide/stores/useDesignGuideStore';
import { capitalizeSentence } from '$/utils/stringUtils';

const TAG_FACETS: HarmonyFacet[] = [
  'mood',
  'tonality',
  'sceneType',
  'colorRange',
];

export const HarmonyFilterTags: FC = () => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const selection = useDesignGuideStore.useSelection();
  const reset = useDesignGuideStore.useReset();

  const { tags, labels } = useMemo(() => {
    const tags: ReactElement[] = [];
    const labels: string[] = [];

    for (const facet of TAG_FACETS) {
      const value = selection[facet];
      if (value === undefined) continue;

      const values = Array.isArray(value) ? value : [value];

      labels.push(values.join(', '));

      tags.push(
        <FilterTag key={facet} onDelete={() => reset(facet)}>
          {values.map((v) => (
            <GenericTagContent
              key={v}
              label={t(`dashboard.designGuide.facets.${facet}.${v}`, {
                defaultValue: capitalizeSentence(v),
              })}
            />
          ))}
        </FilterTag>,
      );
    }

    return { tags, labels };
  }, [selection, reset, t]);

  const {
    invisibleItemCount,
    parentRef,
    getCollectorProps,
    getChildrenProps,
    visibleItemCount,
  } = useCollapsibleList(selection);

  if (tags.length === 0) return null;

  return (
    <Popover
      closeOnBlur={true}
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      placement='bottom-start'
    >
      <HStack ref={parentRef} gap='2' overflowX='hidden'>
        {tags.map((tag, index) => (
          <Box key={tag.key} {...getChildrenProps(index)}>
            {tag}
          </Box>
        ))}

        <Box {...getCollectorProps()}>
          {invisibleItemCount > 0 && (
            <PopoverTrigger>
              <ShowAdditionalFilterTagsButton>
                <GenericTagContent
                  label={`+${invisibleItemCount} Filter`}
                  title={labels
                    .slice(tags.length - invisibleItemCount)
                    .join(', ')}
                />
              </ShowAdditionalFilterTagsButton>
            </PopoverTrigger>
          )}
        </Box>
      </HStack>

      <Portal>
        <PopoverContent
          // the use of sx is necessary because of the !important, which is necessary because chakra overwrites the visibility by themselves
          sx={{
            visibility:
              invisibleItemCount === 0 ? 'hidden !important' : 'visible',
          }}
          w='auto'
          p='4'
          bg='background'
          border='1px solid'
          borderColor='border'
          outline='none'
        >
          <Stack flexWrap='wrap'>
            {tags.slice(visibleItemCount).map((tag) => (
              <Box key={tag.key}>{tag}</Box>
            ))}
          </Stack>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
